/**
 * Arabic vocabulary for the SHADOW EVALUATOR'S UNSENT DRAFT — the reply the overnight automation
 * would have given an enquiry, shown greyed in the inbox thread and never sent to anyone.
 * (Stage 3.5 Increment 4 · tasks/overnight-enquiry-shadow-evaluator.md)
 *
 * Same pattern and same reasoning as `config/relocationLabels.js` and `config/arrivalLabels.js`:
 * the backend sends STABLE CODES and the client owns every word (I18N_STRATEGY — "never infer
 * meaning from message text"). Keeping the words in one importable place is what stops a second
 * surface agreeing on the data while retyping the vocabulary, which is invisible in a diff.
 *
 * ⚠️ ARABIC-ONLY, deliberately, for the same reason relocation is: the inbox is core-staff-only at
 * both the route and the API, and the crew — who English exists for in this product — can never
 * reach it. Bilingual locale files would imply a translated surface that does not exist.
 *
 * ⚠️ «الوحدة», not «الشقة». Ops surfaces across this app say unit; «الشقة» is guest-facing
 * vocabulary and belongs in the reply templates the backend renders, not in what an operator reads.
 */

/**
 * The D160 intent taxonomy — what the guest was ACTUALLY asking.
 *
 * ⚠️ Every `value` MUST match `EnquiryShadowCase.INTENT_LABELS` on the backend. The API validates
 * against an allow-list and REFUSES an unknown value rather than storing nothing and returning 200,
 * so a typo here surfaces as a visible failure rather than a silently unlabelled case.
 */
export const SHADOW_INTENTS = [
  { value: 'same_day_booking', label: 'حجز لنفس اليوم' },
  { value: 'future_booking', label: 'حجز لاحق' },
  { value: 'early_entry', label: 'دخول مبكر' },
  { value: 'availability', label: 'توفر' },
  { value: 'price', label: 'سعر' },
  { value: 'other', label: 'أخرى' },
  { value: 'ambiguous', label: 'غير واضح' }
];

/**
 * Does the guest's wording contradict the dates on their own enquiry (D149)?
 *
 * Three values, not two — «غير واضح» is a real answer, and how often an operator reaches for it is
 * itself one of the findings: it says whether the question is even answerable from the text.
 * Must match `EnquiryShadowCase.CONTRADICTION_VERDICTS`.
 */
export const SHADOW_CONTRADICTIONS = [
  { value: 'DATE_CONTRADICTION', label: 'نعم، تتعارض' },
  { value: 'NO_CONTRADICTION', label: 'لا' },
  { value: 'UNCLEAR', label: 'غير واضح' }
];

/**
 * ⭐ THE GO-LIVE QUESTION — would this draft have been safe to send?
 *
 * Asked FIRST on screen and rated above the intent label, because the first six prod cases all
 * carried different intents and the same safety verdict: a correct availability answer to a question
 * nobody asked. Must match `EnquiryShadowCase.SAFE_TO_AUTOMATE`.
 */
export const SHADOW_SAFE_TO_AUTOMATE = [
  { value: 'yes', label: 'نعم، كان مناسب' },
  { value: 'no', label: 'لا' },
  { value: 'unsure', label: 'غير متأكد' }
];

/**
 * The same three stored values, worded for a case where nothing was drafted. `value` is identical —
 * one field, one dataset; only the words change with the question above it.
 */
export const SHADOW_SAFE_TO_AUTOMATE_SILENT = [
  { value: 'yes', label: 'نعم، عدم الرد صحيح' },
  { value: 'no', label: 'لا، كان يجب الرد' },
  { value: 'unsure', label: 'غير متأكد' }
];

/**
 * Shown only when the answer is «لا». Must match `EnquiryShadowCase.UNSAFE_REASONS`.
 *
 * ⚠️ «خارج النطاق» and «رد على سؤال آخر» look similar and are not: the first is a question we will
 * never automate, the second is a question we do automate where the draft answered the wrong part of
 * it. They point at different fixes, so they are counted apart.
 */
/** Shown under «لا» on a SILENT case: why should we have answered? */
export const SHADOW_SHOULD_HAVE_ANSWERED_REASONS = [
  { value: 'out_of_scope', label: 'كان داخل النطاق فعلاً' },
  { value: 'wrong_question', label: 'قرأ السؤال خطأ' },
  { value: 'wrong_facts', label: 'المعلومة متوفرة لدينا' },
  { value: 'dates_contradicted', label: 'سبب متعلق بالتواريخ' },
  { value: 'other', label: 'سبب آخر' }
];

export const SHADOW_UNSAFE_REASONS = [
  { value: 'out_of_scope', label: 'خارج النطاق' },
  // ⚠️ A NOUN PHRASE, not «رد على سؤال آخر» — undiacritised that reads first as an imperative
  // ("reply to another question"), and it was the only verb form in a row of noun phrases.
  { value: 'wrong_question', label: 'إجابة عن سؤال آخر' },
  { value: 'wrong_facts', label: 'معلومة غير صحيحة' },
  { value: 'dates_contradicted', label: 'تعارض مع التواريخ' },
  { value: 'other', label: 'سبب آخر' }
];

/**
 * The block's own screen text. Here rather than inline in the page for the reason at the top of
 * this file: a config module that holds only the chip labels no longer describes the surface, and
 * the next person to change a question changes it in one of two places.
 */
export const SHADOW_QUESTIONS = {
  // ⚠️ TWO WORDINGS, AND THE DISTINCTION IS NOT COSMETIC. Roughly five cases in six are a SILENCE,
  // and "was it appropriate to send this reply?" is unanswerable when nothing was drafted — an
  // operator can read it either way, and this is the field the go-live decision rests on. On a
  // silence the question we actually need is whether staying quiet was right. Found on the first
  // live out-of-scope case (Alisha / P-0008, 29 Aug).
  safe: 'هل كان مناسب إرسال هذا الرد؟',
  safeSilent: 'هل كان عدم الرد هو التصرف الصحيح؟',
  unsafeWhy: 'لماذا؟',
  intent: 'ماذا كان الضيف يسأل عنه؟',
  contradiction: 'هل تتعارض كلمات الضيف مع تواريخ الطلب؟'
};

/**
 * What the drafted sentence actually is.
 *
 * ⚠️ WORDED SO IT STAYS TRUE. An earlier version said «لم يُقرأ سؤال الضيف» — accurate today, but a
 * hardcoded claim about a gate that is due to change: once the scope assessment ships, the screen
 * would tell the operator the system ignored the guest's question when it had not, and they would
 * mark safe drafts unsafe in the one dataset the go-live decision rests on. It was also wrong for a
 * stay-restriction draft, where availability is not the determining fact. This wording is
 * guaranteed permanently by D138 — the answer is always composed from stored facts, never written
 * from the guest's words — so it cannot rot.
 *
 * ⚠️ «والتواريخ» was dropped once tier-1 answers shipped: a parking or wifi reply is built from the
 * unit's data and no dates at all, so naming them made the line false on the majority of drafts.
 */
export const SHADOW_DRAFT_NOTE = 'صيغة جاهزة، رُكِّبت من بيانات الوحدة';

/** Shown when an answer fails to save. Codes, translated here — the client owns the wording. */
export const SHADOW_SAVE_ERRORS = {
  ENQUIRY_SHADOW_STALE: 'تم تحديث طلب الضيف — يرجى إعادة تحميل المحادثة',
  ENQUIRY_SHADOW_REASON_WITHOUT_NO: 'لا يمكن تحديد السبب إلا عند اختيار «لا»',
  DEFAULT: 'تعذّر حفظ التصنيف، يرجى المحاولة مرة أخرى'
};

/**
 * WHAT THE SCOPE ASSESSMENT READ THE GUEST AS ASKING (D187). Keys are the classifier's own stable
 * codes — the twelve supported topics plus the two refusals.
 *
 * ⚠️ «خارج النطاق» and «غير واضح» are NOT errors and must not read as ones: an out-of-scope
 * question routing to a human is the system working exactly as designed (D190), and how often it
 * happens is the commercial number the shadow period produces.
 */
export const SHADOW_SCOPE_AR = {
  availability: 'توفر',
  checkin_time: 'وقت تسجيل الدخول',
  checkout_time: 'وقت تسجيل الخروج',
  parking_general: 'وجود موقف',
  parking_cost: 'تكلفة الموقف',
  wifi_exists: 'واي فاي',
  self_checkin: 'دخول ذاتي',
  elevator: 'مصعد',
  pool: 'مسبح',
  gym: 'صالة رياضية',
  washing_machine: 'غسالة',
  kitchen: 'مطبخ',
  // D200 — asked about RIGHT NOW. Kept apart from «توفر» so the review can count the two questions
  // separately; both are answered by the same gate.
  current_entry: 'دخول الآن',
  // D201 — the one approved combination: both times in a single answer.
  checkin_checkout_times: 'وقتا الدخول والخروج',
  // D203 — recognised so a human is raised, NEVER answered. The wording says asked-about, not
  // answered, because nothing here ever reaches a draft.
  price_question: 'سؤال عن السعر',
  discount_request: 'طلب خصم',
  out_of_scope: 'خارج النطاق',
  unclear: 'غير واضح'
};

/** Same rule as `shadowReasonAr`: an unmapped code stays VISIBLE, never hidden behind a vague word. */
export const shadowScopeAr = (code) => {
  if (!code) return null;
  return SHADOW_SCOPE_AR[code] || `موضوع غير معروف (${code})`;
};

/**
 * The whole line, composed HERE rather than concatenated in the page.
 *
 * ⚠️ NOT «قرأه النظام كـ …», which was the first cut. «كـ» is the kāf of comparison and takes a
 * noun: it is ungrammatical on «خارج النطاق» (adverbial) and «غير واضح» (a negation particle) —
 * and those two are the MAJORITY outcome by design, so the broken form would be the common one. The
 * tatweel also exists to attach to the following word, and JSX concatenation put a space after it,
 * which is orthographically wrong on all fourteen values and lets a wrap strand it on its own line.
 * A colon label is correct for every value and composes as one string, so it cannot be re-broken.
 */
export const shadowScopeLine = (code) => {
  const ar = shadowScopeAr(code);
  return ar ? `قراءة النظام: ${ar}` : null;
};

/**
 * WHY THE SYSTEM WOULD HAVE STAYED SILENT. Keys are the gate's own reason codes (D170).
 *
 * ⚠️ Includes the two ANSWER-shaped codes. They normally accompany a real draft, but an answer we
 * cannot put into words is still a silence, and a code with no Arabic beside it would print raw
 * English into an RTL sentence.
 */
export const SHADOW_REASONS_AR = {
  INELIGIBLE_NO_APARTMENT: 'لم نتمكن من تحديد الوحدة',
  INELIGIBLE_AUTOMATION_DISABLED: 'التشغيل الآلي غير مفعّل لهذه الوحدة',
  INELIGIBLE_LOCK: 'القفل غير جاهز',
  INELIGIBLE_ALREADY_ANSWERED: 'تم الرد على هذا الطلب مسبقاً',
  ANSWERED_UNAVAILABLE: 'الوحدة محجوزة في التواريخ المطلوبة',
  ANSWERED_RESTRICTED: 'مدة الإقامة المطلوبة غير متاحة',
  UNKNOWN_DATES: 'لا توجد تواريخ في الطلب',
  UNKNOWN_AMBIGUOUS_DATES: 'التواريخ غير واضحة',
  UNKNOWN_AVAILABILITY: 'تعذّر التحقق من التوفر',
  UNKNOWN_CLEANING: 'تعذّر التحقق من النظافة',
  UNKNOWN_OCCUPANCY: 'تعذّر التحقق من وجود ضيف حالياً',
  UNKNOWN_LOCK_STATE: 'حالة القفل غير معروفة',
  UNKNOWN_PROPERTY: 'تعذّر تحميل بيانات الوحدة',
  UNKNOWN_LOOKUP_FAILED: 'تعذّر إكمال الفحص',
  UNRENDERABLE_TEMPLATE: 'تعذّر صياغة رد صحيح من المعطيات',
  // ── D187/D190 — silences about the QUESTION rather than the apartment ──
  SUPPRESSED_OUT_OF_SCOPE: 'السؤال خارج ما يرد عليه النظام آلياً',
  SUPPRESSED_UNCLEAR_INTENT: 'لم يتضح ما يسأل عنه الضيف',
  // D203 — the point of these two is that they are NOT the same silence as «خارج النطاق». Both stay
  // quiet; only these are a commercial opportunity somebody should be woken for, and the wording has
  // to make an operator reading the queue see the difference at a glance.
  HUMAN_REQUIRED_PRICE_QUESTION: 'سؤال عن السعر — يحتاج ردًا بشريًا، والنظام لا يذكر أسعارًا أبدًا',
  HUMAN_REQUIRED_DISCOUNT_REQUEST: 'طلب خصم — قرار تجاري، يحتاج ردًا بشريًا',
  // D209 — a colleague replied, so the thread is theirs. NOT "the question was answered": the reply
  // may well have left it open, and the system stops either way.
  SUPPRESSED_HUMAN_HANDOFF: 'ردّ أحد الزملاء على المحادثة — تسلّمها فريقنا',
  UNKNOWN_SCOPE: 'تعذّر تحديد موضوع السؤال',
  // ⚠️ NOT «لا يوجد» — we never deny a fact (D191). This says the box is not ticked, which is a
  // gap in our own data and an instruction to ops, not an answer about the unit.
  FACT_NOT_RECORDED: 'المعلومة غير مسجّلة في بيانات الوحدة',
  // ⚠️ WORDED AS THE FAULT IT IS. This code means the classifier named a topic the templates do not
  // have — deploy skew, a bug. Worded as a product limitation it reads as normal and gets accepted
  // rather than escalated, which is how a whole topic goes quietly unanswered for weeks.
  UNKNOWN_FACT_INTENT: 'خلل داخلي — موضوع غير معرّف في النظام'
};

/**
 * ⚠️ AN UNMAPPED CODE STAYS VISIBLE, but never as a bare English token in an Arabic sentence.
 *
 * The tempting fallback is a vague «سبب آخر» — the pattern `automatedLabel` uses for message types.
 * It is wrong here: during a shadow period an unrecognised reason is exactly the finding worth
 * noticing, and hiding it is how a whole class of silence goes uncounted for weeks. So the sentence
 * stays Arabic and the raw code rides along in parentheses for whoever is reading the screen.
 */
export const shadowReasonAr = (code) => {
  if (!code) return null;
  return SHADOW_REASONS_AR[code] || `سبب غير معروف (${code})`;
};

/** Stability, in words. Colour is only ever the SECOND signal — see InboxPage.css. */
export const SHADOW_STABILITY = {
  held: 'بقي صحيحاً حتى وقت الإرسال',
  broke: 'تغيّر قبل وقت الإرسال',
  unverifiable: 'تعذّر إعادة الفحص',
  pending: 'لم تتم إعادة الفحص بعد'
};

export const SHADOW_OVERNIGHT_TAG = 'وصل ليلاً';

/**
 * THE COLLAPSED LINE. The block is collapsed by default on every screen — on a phone it was the
 * whole viewport, so opening a thread showed a labelling form instead of the guest's message, which
 * inverts what the inbox is for.
 *
 * ⚠️ THE UNLABELLED WORDING CARRIES THE PROMPT, and that is the point rather than decoration. This
 * product has already lost a dataset to a field nobody was asked to fill (0 of 12 early check-in
 * states). Hiding the form behind a bare chevron would repeat it — the labels ARE the shadow period.
 */
export const SHADOW_COLLAPSED = {
  title: 'الرد المقترح — لم يُرسل',
  needsLabel: 'بحاجة إلى تصنيف',
  labelled: 'تم التصنيف',
  expand: 'عرض التفاصيل',
  collapse: 'إخفاء التفاصيل'
};
